import type {
  RealtimeChannel,
  SupabaseClient,
} from '@supabase/supabase-js';
import type {
  Action,
  GameEvent,
  GameState,
  MapDefinition,
  PlayerId,
} from '../game/types.js';
import { playerId as toPlayerId } from '../game/types.js';
import { applyAction } from '../game/actions.js';
import { validateAction } from '../game/validation.js';
import { mapsById } from '../maps/index.js';
import { apiBaseUrl } from '../platform/index.js';
import { getAuth } from './auth.js';
import type {
  ConnectionListener,
  ConnectionStatus,
  DispatchResult,
  Store,
  StoreListener,
} from './Store.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Minimum gap between visibility-triggered refetches. Tab-switching
 * shouldn't hammer the API; 3 seconds is a reasonable floor for
 * "I just glanced away and came back."
 */
const VISIBILITY_REFETCH_DEBOUNCE_MS = 3000;

/**
 * Network-backed store. Mutations are POST'd to /api/games/:id/actions
 * and the response (or a Realtime broadcast from Postgres) updates
 * local state. Implements the same Store interface as the local
 * GameStore so GameScene can drive either one.
 *
 * Dispatch flow:
 *   1. Client validates locally for fast rejection (no network)
 *   2. POSTs the action to the server
 *   3. Server validates again, applies, persists, returns new state
 *   4. We apply that snapshot and fire subscribers
 *   5. Realtime delivers the same snapshot to OTHER players' clients
 *
 * Server is authoritative — local validation is a UX optimization
 * (catch silly inputs before the round trip), not a security check.
 */
export class RemoteGameStore implements Store {
  private state: GameState;
  /** v0.9.9 Hourglass — ISO timestamp when current turn auto-skips.
   *  Mirrors RemoteGameSnapshot.turnExpiresAt; updated from
   *  realtime UPDATEs and the periodic refetch path. */
  private turnExpiresAt: string | null;
  private channel: RealtimeChannel | null = null;
  private readonly listeners = new Set<StoreListener>();
  private readonly connectionListeners = new Set<ConnectionListener>();
  // Dedupe key: every successful action appends a monotonic
  // LogEntry.id to state.history. Skip incoming snapshots whose
  // latest history id is ≤ the one we last applied — that filters
  // the Realtime echo of our own POST AND any out-of-order
  // Realtime deliveries.
  private lastAppliedHistoryId: number;

  // FIFO action queue. Every dispatch enqueues; a single in-flight
  // POST drains the queue one action at a time. Serializing avoids
  // the parallel-Vercel-function-instance race where two POSTs read
  // the same starting state and one's write overwrites the other.
  //
  // Each entry tracks whether the action was already optimistically
  // applied to `this.state` at dispatch time. The queue processor
  // uses this to decide whether to re-validate against current state
  // before POSTing — actions that already applied locally must NOT
  // be re-validated (this.state is the POST-apply state, so the
  // action would always appear invalid; the legitimate validity
  // check happened at dispatch time against pre-apply state).
  private readonly actionQueue: Array<{
    readonly action: Action;
    readonly alreadyApplied: boolean;
  }> = [];
  private processingQueue = false;

  // Connection state. Starts 'reconnecting' so callers don't briefly
  // see 'connected' before SUBSCRIBED actually fires; the first
  // SUBSCRIBED callback flips it to 'connected'.
  private connectionStatus: ConnectionStatus = 'reconnecting';
  // True after the FIRST SUBSCRIBED callback. We use this to detect
  // "this SUBSCRIBED is a reconnect, not the initial connection" —
  // reconnects need a snapshot refetch to catch up on whatever
  // happened while we were offline.
  private hasEverSubscribed = false;
  // Debounce tracking for visibility-triggered refetches.
  private lastVisibilityRefetchAt = 0;
  // Bound handler so addEventListener / removeEventListener match.
  private readonly visibilityHandler = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      void this.refetchOnVisibility();
    }
  };

  constructor(
    initialState: GameState,
    private readonly map: MapDefinition,
    private readonly gameId: string,
    private readonly playerId: PlayerId,
    private readonly supabase: SupabaseClient,
    initialTurnExpiresAt: string | null = null
  ) {
    this.state = initialState;
    this.turnExpiresAt = initialTurnExpiresAt;
    this.lastAppliedHistoryId = latestHistoryId(initialState);
    this.subscribeRealtime();
    // Visibility refresh: when the user returns to the tab/window
    // after backgrounding, the Realtime channel may have dropped
    // and reconnected without firing any events. A snapshot refetch
    // here catches anything the live channel missed.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  getState(): GameState {
    return this.state;
  }

  getMap(): MapDefinition {
    return this.map;
  }

  /** Who am I in this game? Used by the UI to gate "is it my turn." */
  getPlayerId(): PlayerId {
    return this.playerId;
  }

  isMultiplayer(): boolean {
    return true;
  }

  isLocalTurn(): boolean {
    return this.state.turn.currentPlayer === this.playerId;
  }

  isLocalPlayer(playerId: PlayerId): boolean {
    // Only THIS browser's seat is "you" in multiplayer — other humans
    // are opponents, even when both are humans.
    return playerId === this.playerId;
  }

  dispatch(action: Action): DispatchResult {
    // Refuse to send an action claiming to be a different player. The
    // server checks this too, but failing fast keeps the wire clean.
    if (action.playerId !== this.playerId) {
      return {
        ok: false,
        reason: 'cannot act as another player',
      };
    }
    // Local validation matches what the server will do. If we reject
    // here, the action never leaves the browser.
    const validation = validateAction(this.state, action, this.map);
    if (!validation.ok) return { ok: false, reason: validation.reason };

    // Optimistic local apply for deterministic actions. The user
    // sees their tile update INSTANTLY instead of after a 200-500ms
    // server roundtrip. applyAction is the same pure module the
    // server runs, so identical starting state → identical result;
    // rollback is rare in practice.
    //
    // Two action types deliberately stay server-roundtrip:
    //   - 'attack' rolls dice with state.rngSeed, but the client
    //     receives stripped state (rngSeed=0 sentinel). A local
    //     apply would compute wrong dice + wrong outcome and the
    //     server snapshot would visibly contradict it. The dice
    //     animation in GameScene.handleAttackTap already starts
    //     a rolling-shim immediately for perceived responsiveness.
    //   - 'endTurn' chains AI turns server-side; the server's
    //     last_events includes BOTH the turnEnded event AND every
    //     AI action's events. Applying locally would queue a
    //     turnEnded animation, then the server snapshot would
    //     replay it. Single roundtrip avoids the double-animation.
    //   - 'endPhase' during turn 0 (setup round) ALSO transitions
    //     the player and can chain AI setup-deploys, so it gets
    //     the same treatment in the setup round only. After turn 0
    //     endPhase is just deploy→attack or attack→fortify on the
    //     same player — safe to apply locally.
    // Gate optimistic apply on "queue has no non-optimistic action
    // pending." If an attack or endTurn (or turn-0 endPhase) is mid-
    // roundtrip, the client's `this.state` is stale relative to what
    // the server will see — applying any optimistic action against
    // it would mean speculating against a phantom past. The user's
    // action could be valid now (in pre-attack land) but invalid
    // moments later when the attack's `awaitingAdvance` lands.
    //
    // Paired with the re-validation guard in processActionQueue that
    // drops actions which became invalid between enqueue and POST.
    // Together they eliminate the "tap-deploy-during-attack-roundtrip"
    // race that produced a brief deploy flicker + a server-side 400
    // "Must resolve advance before any other action."
    const hasPendingNonOptimistic = this.actionQueue.some(
      (entry) =>
        entry.action.type === 'attack' ||
        entry.action.type === 'endTurn' ||
        (entry.action.type === 'endPhase' && this.state.turnNumber === 0)
    );

    const optimisticAllowed =
      !hasPendingNonOptimistic &&
      (action.type === 'deploy' ||
        action.type === 'advance' ||
        action.type === 'fortify' ||
        (action.type === 'endPhase' && this.state.turnNumber > 0));

    if (optimisticAllowed) {
      const optimistic = applyAction(this.state, action, this.map);
      this.state = optimistic.state;
      this.lastAppliedHistoryId = latestHistoryId(this.state);
      this.enqueueAction(action, /* alreadyApplied */ true);
      // notify AFTER enqueue so listeners observing the new state
      // are also observing a queue that already has this action
      // logged. Order-pedantry — no behavior difference today.
      this.notify(optimistic.events);
      return {
        ok: true,
        state: this.state,
        events: optimistic.events,
      };
    }

    // Non-optimistic path (attack / endTurn / deferred optimistic):
    // defer to the server. Subscribers update when the snapshot arrives.
    this.enqueueAction(action, /* alreadyApplied */ false);
    return { ok: true, state: this.state, events: [] };
  }

  /**
   * Append an action to the post queue and kick the processor if
   * idle. Every dispatch funnels through here so the network layer
   * sees exactly one in-flight POST at a time.
   *
   * `alreadyApplied` tracks whether the action was optimistically
   * applied to `this.state` at dispatch time. Used by the queue
   * processor to decide whether to re-validate against current
   * state — re-validation is meaningful only for actions that
   * DIDN'T apply locally (because `this.state` shows what the
   * server should have, so a now-invalid action is genuinely stale).
   */
  private enqueueAction(action: Action, alreadyApplied: boolean): void {
    this.actionQueue.push({ action, alreadyApplied });
    if (!this.processingQueue) {
      void this.processActionQueue();
    }
  }

  /**
   * Drain the action queue one POST at a time. Reentrancy-safe via
   * `processingQueue`; the second concurrent caller no-ops because
   * the first is already draining.
   */
  private async processActionQueue(): Promise<void> {
    if (this.processingQueue) return;
    this.processingQueue = true;
    try {
      while (this.actionQueue.length > 0) {
        const entry = this.actionQueue.shift()!;
        // Re-validate only for actions that did NOT apply locally at
        // dispatch time. For those, `this.state` reflects what the
        // server should currently see, so a now-invalid action is
        // genuinely stale (most common cause: dispatch landed during a
        // non-optimistic roundtrip and applyServerSnapshot moved state
        // forward before we POSTed). Drop silently — the server-truth
        // snapshot has already shifted the UI to the new context, so
        // the user sees the correct affordance and can retap.
        //
        // For already-applied actions we MUST NOT re-validate:
        // `this.state` is the POST-apply state, so the action would
        // always appear invalid against it ("you already moved past
        // the phase that action transitions"). Those actions had
        // their legitimate validation at dispatch time against the
        // pre-apply state — they're safe to POST as-is. If the
        // server rejects them after all, the refetch-on-rejection
        // path corrects the divergence.
        if (!entry.alreadyApplied) {
          const reval = validateAction(this.state, entry.action, this.map);
          if (!reval.ok) {
            console.warn(
              'dropping queued action — state advanced before POST',
              { actionType: entry.action.type, reason: reval.reason }
            );
            continue;
          }
        }
        await this.postAction(entry.action);
      }
    } finally {
      this.processingQueue = false;
    }
  }

  subscribe(fn: StoreListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Tear down the Realtime subscription. Call on scene shutdown. */
  destroy(): void {
    if (this.channel) {
      void this.supabase.removeChannel(this.channel);
      this.channel = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.listeners.clear();
    this.connectionListeners.clear();
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  onConnectionChange(fn: ConnectionListener): () => void {
    this.connectionListeners.add(fn);
    return () => {
      this.connectionListeners.delete(fn);
    };
  }

  // --- Internals ---

  private async postAction(action: Action): Promise<void> {
    try {
      const res = await fetch(`${apiBaseUrl()}/api/games/${this.gameId}/actions`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify({ action }),
      });
      const data = (await res.json()) as {
        state?: GameState;
        events?: readonly GameEvent[];
        error?: string;
        detail?: string;
      };
      if (!res.ok) {
        console.error(
          'server rejected action',
          data.error,
          data.detail
        );
        // Local state may now diverge from server (e.g., we
        // optimistically applied a deploy and the server rejected
        // it for reasons local validation missed — game ended,
        // turn changed, etc.). Refetch the authoritative snapshot
        // to correct the divergence. dedup-by-history-id makes
        // this a no-op when the local state already matches.
        void this.refetchAndApply('rejected-action');
        return;
      }
      if (data.state) {
        // applyServerSnapshot dedupes against lastAppliedHistoryId,
        // so when an optimistic action's response arrives the echo
        // is silently dropped (we already applied that history).
        // Non-optimistic action responses (attack / endTurn) carry
        // a higher history id and apply normally, plus any chained
        // AI actions for endTurn.
        this.applyServerSnapshot(data.state, data.events ?? []);
      }
    } catch (err) {
      console.error('action POST failed', err);
      // Network failure leaves local state ahead of server. The
      // visibility / reconnect refetch handlers will pull truth
      // when connectivity returns; nothing to do synchronously.
    }
  }

  private applyServerSnapshot(
    state: GameState,
    events: readonly GameEvent[]
  ): void {
    const incomingId = latestHistoryId(state);
    if (incomingId <= this.lastAppliedHistoryId) {
      // Echo of an already-applied action OR an out-of-order
      // Realtime payload — skip to avoid double-firing animations.
      return;
    }
    this.lastAppliedHistoryId = incomingId;
    this.state = state;
    this.notify(events);
  }

  private subscribeRealtime(): void {
    this.channel = this.supabase
      .channel(`game-${this.gameId}`)
      .on(
        'postgres_changes' as never, // type union upstream is too narrow
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${this.gameId}`,
        },
        (payload: {
          new?: {
            state?: GameState;
            last_events?: readonly GameEvent[];
            last_action_at?: string;
          };
        }) => {
          const next = payload.new?.state;
          if (!next) return;
          const events = payload.new?.last_events ?? [];
          // v0.9.9 Hourglass — recompute the turn timer client-side
          // from the row's fresh last_action_at. The cron and every
          // action handler stamp this column on update, so realtime
          // always carries the right value.
          if (typeof payload.new?.last_action_at === 'string') {
            this.turnExpiresAt = computeTurnExpiresAtFromState(
              next,
              payload.new.last_action_at
            );
          }
          // Skip the broadcast that echoes our own action — we've
          // already applied that snapshot from the POST response.
          // Detected by comparing rngSeed; combat advances it, other
          // actions leave it untouched but the listener is idempotent
          // either way.
          this.applyServerSnapshot(next, events);
        }
      )
      // Status callback. Supabase auto-reconnects on its own; we use
      // this hook to (a) surface live connection state to the UI and
      // (b) refetch the snapshot whenever SUBSCRIBED fires so we
      // never get stuck on a stale state.
      //
      // Why ALWAYS refetch (not just on reconnect):
      //   The initial connection has a small window between
      //   `fetchGameSnapshot()` returning and `.subscribe()` finishing
      //   the handshake. If another player pushes an UPDATE during
      //   that window, we never receive it — the channel wasn't
      //   listening yet. Reload-to-fix was the only workaround.
      //   Refetching on initial SUBSCRIBED closes that gap.
      //
      // The refetch is a single HTTP call; on a normal connection
      // it's a few hundred ms. applyServerSnapshot dedupes by
      // history id so a no-op snapshot doesn't fire spurious events.
      .subscribe((status: string, err?: Error) => {
        if (status === 'SUBSCRIBED') {
          this.setConnectionStatus('connected');
          // Fire-and-forget; failures stay in 'connected' since the
          // live channel itself is healthy.
          void this.refetchAndApply(
            this.hasEverSubscribed ? 'reconnect' : 'initial'
          );
          this.hasEverSubscribed = true;
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          if (err) {
            console.warn(`Realtime ${status} on game-${this.gameId}`, err);
          }
          this.setConnectionStatus('reconnecting');
        }
      });
  }

  private setConnectionStatus(next: ConnectionStatus): void {
    if (this.connectionStatus === next) return;
    this.connectionStatus = next;
    for (const fn of this.connectionListeners) {
      try {
        fn(next);
      } catch (e) {
        console.error('Store connection listener threw:', e);
      }
    }
  }

  private async refetchOnVisibility(): Promise<void> {
    const now = Date.now();
    if (now - this.lastVisibilityRefetchAt < VISIBILITY_REFETCH_DEBOUNCE_MS) {
      return;
    }
    this.lastVisibilityRefetchAt = now;
    await this.refetchAndApply('visibility');
  }

  private async refetchAndApply(
    reason: 'reconnect' | 'visibility' | 'initial' | 'rejected-action'
  ): Promise<void> {
    try {
      const snap = await fetchGameSnapshot(this.gameId);
      if (!snap.state) return; // game went back to lobby? extremely unlikely
      // v0.9.9 — refresh turnExpiresAt from the snapshot (it's the
      // authoritative server-computed value at fetch time).
      this.turnExpiresAt = snap.turnExpiresAt;
      // applyServerSnapshot dedupes by history id, so a snapshot
      // older than what we've already applied locally is a no-op.
      this.applyServerSnapshot(snap.state, []);
    } catch (err) {
      console.warn(`snapshot refetch (${reason}) failed`, err);
    }
  }

  private notify(events: readonly GameEvent[]): void {
    for (const fn of this.listeners) {
      try {
        fn(this.state, events);
      } catch (e) {
        console.error('Store listener threw:', e);
      }
    }
  }

  getTurnExpiresAt(): string | null {
    return this.turnExpiresAt;
  }
}

/**
 * Client-side mirror of computeTurnExpiresAt from src/server/turnExpiry.
 * Lives here so it can run from realtime payloads without pulling
 * server-only deps. Kept in sync with the server helper. v0.9.9.
 */
const TURN_DURATION_MS = 24 * 60 * 60 * 1000;
function computeTurnExpiresAtFromState(
  state: GameState,
  lastActionAt: string
): string | null {
  if (state.status?.kind !== 'inProgress') return null;
  const currentPlayer = state.turn?.currentPlayer;
  if (!currentPlayer) return null;
  if (currentPlayer.startsWith('ai-')) return null;
  const lastMs = new Date(lastActionAt).getTime();
  if (Number.isNaN(lastMs)) return null;
  return new Date(lastMs + TURN_DURATION_MS).toISOString();
}

/** Most-recent LogEntry id in this state, or -1 if history is empty. */
function latestHistoryId(state: GameState): number {
  const h = state.history;
  return h.length === 0 ? -1 : h[h.length - 1]!.id;
}

// --- Factory + helpers ---

/** Game mode chosen at create time — drives lobby caps and team math. */
export type RemoteGameMode = 'ffa' | '2v2-ai';

export interface RemoteGameSnapshot {
  readonly id: string;
  readonly mapId: string;
  readonly mode: RemoteGameMode;
  readonly status: 'lobby' | 'active' | 'finished';
  readonly state: GameState | null;
  /** Auto-start threshold from games.target_seats. Drives the lobby
   *  "Waiting for X more players..." display. Backfilled per mode for
   *  pre-0016 games; populated explicitly on every fresh insert. */
  readonly targetSeats: number;
  readonly seats: readonly RemoteGameSeat[];
  /** ISO timestamp when the CURRENT player's turn auto-skips —
   *  derived server-side from `last_action_at + 24h`. Null when
   *  status !== 'active' (no live turn timer) or the current
   *  player is an AI seat. Drives the action-panel timer chip.
   *  v0.9.9 Hourglass. */
  readonly turnExpiresAt: string | null;
}

export interface RemoteGameSeat {
  readonly seat_index: number;
  /** NULL for AI seats (no auth.users row). Humans have their auth
   *  user id here; identity comparisons treat null as "not me." */
  readonly user_id: string | null;
  readonly name: string;
  readonly color: string;
  readonly kind: 'human' | 'ai';
  readonly team: number;
}

/**
 * Build the Authorization header for an API call. Returns an empty
 * object if there's no session — caller still sends the request, but
 * the server will return 401 since requireUserForMp will reject it.
 */
function authHeader(): Record<string, string> {
  const token = getAuth().session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch the current game snapshot via GET /api/games/:id. Used by
 * JoinScene to render the lobby (status + seats) and by the GameScene
 * boot to grab initial state before opening the Realtime channel.
 *
 * Auth-gated post-Slice 1.3 — the server returns 404 when the caller
 * isn't seated in the game. Returns the unfortunate status code in
 * the thrown error so JoinScene can distinguish "didn't authenticate"
 * (401) from "you're not in this game" (404) and "real server error"
 * (5xx).
 */
export async function fetchGameSnapshot(
  gameId: string
): Promise<RemoteGameSnapshot> {
  const res = await fetch(`${apiBaseUrl()}/api/games/${gameId}`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const wrapped = new Error(
      `load game ${gameId} failed (${res.status}): ${JSON.stringify(err)}`
    );
    (wrapped as Error & { status?: number }).status = res.status;
    throw wrapped;
  }
  return (await res.json()) as RemoteGameSnapshot;
}

/**
 * Fetch derived stats for a game via GET /api/games/:id/stats. The
 * server replays the action log + initial seed and aggregates dice
 * histograms, combat outcomes, deploys, conquests, and per-player
 * tile counts. Used by ResultsScene to populate the stats card.
 *
 * Returns the parsed body and HTTP status so the caller can distinguish:
 *   - 200 → real stats
 *   - 422 → game can't be replayed; body has `reason` ('lobby',
 *           'no_initial_seed', 'unknown_map') for copy selection
 *   - 401/403/404/5xx → throws with .status set
 */
export async function fetchGameStats(gameId: string): Promise<
  | { kind: 'ok'; stats: import('../api/stats-types.js').GameStats }
  | {
      kind: 'unavailable';
      reason: import('../api/stats-types.js').StatsUnavailable['reason'];
    }
> {
  const res = await fetch(`${apiBaseUrl()}/api/games/${gameId}/stats`, {
    headers: { ...authHeader() },
  });
  if (res.status === 422) {
    const body = (await res
      .json()
      .catch(() => ({}))) as Partial<
      import('../api/stats-types.js').StatsUnavailable
    >;
    return {
      kind: 'unavailable',
      reason: body.reason ?? 'no_initial_seed',
    };
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const wrapped = new Error(
      `load stats ${gameId} failed (${res.status}): ${JSON.stringify(err)}`
    );
    (wrapped as Error & { status?: number }).status = res.status;
    throw wrapped;
  }
  const stats = (await res.json()) as import('../api/stats-types.js').GameStats;
  return { kind: 'ok', stats };
}

/**
 * Build a fully wired RemoteGameStore from a game that's already in
 * the `active` state. Throws if the snapshot doesn't have a state yet
 * (game is still in lobby) or if there's no authenticated session
 * (post-Slice 1.3, MP requires a signed-in user).
 */
export function createRemoteGameStore(
  snapshot: RemoteGameSnapshot
): RemoteGameStore {
  if (!snapshot.state) {
    throw new Error(
      'cannot create RemoteGameStore: game still in lobby (no state)'
    );
  }
  const map = mapsById[snapshot.mapId];
  if (!map) {
    throw new Error(`unknown mapId on game: ${snapshot.mapId}`);
  }
  const auth = getAuth();
  if (!auth.user) {
    throw new Error('cannot create RemoteGameStore: not signed in');
  }
  return new RemoteGameStore(
    snapshot.state,
    map,
    snapshot.id,
    toPlayerId(auth.user.id),
    supabaseBrowser(),
    snapshot.turnExpiresAt
  );
}
